import type {Mapper} from 'framework';
import type {DailyDashboard, DashboardMealGroup} from '../../domain/daily-dashboard';
import type {DashboardMeal} from '../../domain/dashboard-meal';
import type {DashboardMealEntry} from '../../domain/dashboard-meal-entry.value-object';
import type {DailyDashboardDto} from '../dto/daily-dashboard.dto';

type MealGroupDto = DailyDashboardDto['mealsByType'][number];
type MealDto = MealGroupDto['meals'][number];
type MealEntryDto = MealDto['entries'][number];

const toEntry = (entry: MealEntryDto): DashboardMealEntry => ({
  id: entry.id,
  displayName: entry.displayName,
  quantity: entry.quantity,
  unit: entry.unit,
  gramEquivalent: entry.gramEquivalent,
  kcal: entry.kcal,
  proteinGrams: entry.proteinGrams,
  carbsGrams: entry.carbsGrams,
  fatGrams: entry.fatGrams,
  source: entry.source,
});

const toMeal = (meal: MealDto): DashboardMeal => ({
  id: meal.id,
  mealType: meal.mealType,
  consumedAt: meal.consumedAt,
  totals: {
    kcal: meal.totals.kcal,
    proteinGrams: meal.totals.proteinGrams,
    carbsGrams: meal.totals.carbsGrams,
    fatGrams: meal.totals.fatGrams,
    ...(meal.totals.kcalCalculatedFromMacros !== undefined && {
      kcalCalculatedFromMacros: meal.totals.kcalCalculatedFromMacros,
    }),
  },
  entries: meal.entries.map(toEntry),
  editedAt: meal.editedAt,
});

const toGroup = (group: MealGroupDto): DashboardMealGroup => ({
  mealType: group.mealType,
  meals: group.meals.map(toMeal),
});

/**
 * Translates the RPC DTO into the dashboard slice's domain aggregate.
 * `targets`/`progress` use `null` (not `undefined`) in the domain so
 * downstream code can rely on a simple `=== null` check.
 */
export class DailyDashboardDtoToDailyDashboardMapper implements Mapper<
  DailyDashboardDto,
  DailyDashboard
> {
  transform(input: DailyDashboardDto): DailyDashboard {
    return {
      date: input.date,
      totals: {
        kcal: input.totals.kcal,
        proteinGrams: input.totals.proteinGrams,
        carbsGrams: input.totals.carbsGrams,
        fatGrams: input.totals.fatGrams,
        ...(input.totals.kcalCalculatedFromMacros !== undefined && {
          kcalCalculatedFromMacros: input.totals.kcalCalculatedFromMacros,
        }),
      },
      targets: input.targets ?? null,
      progress: input.progress ?? null,
      mealsByType: input.mealsByType.map(toGroup),
    };
  }
}
